import { inject, Injectable } from '@angular/core';

import type { FormlyFieldConfig } from '@ngx-formly/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import type { Observable } from 'rxjs';
import { switchMap } from 'rxjs';

import { RECAPTCHA_V2_SITE_KEY } from '@valk-nx/core/lib/core';

@Injectable({
	providedIn: 'root',
})
export class RecaptchaFormService {
	private readonly recaptchaV2Key = inject(RECAPTCHA_V2_SITE_KEY);
	private readonly recaptchaV3Service = inject(ReCaptchaV3Service);

	// Wrap API calls with reCAPTCHA protection, if v2Token is provided, use it, otherwise use v3
	withRecaptcha<T>(
		action: string,
		operation: (token: string, version: 'v2' | 'v3') => Observable<T>,
		v2Token?: string,
	): Observable<T> {
		if (v2Token && v2Token.length > 0) {
			return operation(v2Token, 'v2');
		}

		return this.recaptchaV3Service
			.execute(action)
			.pipe(switchMap((v3Token: string) => operation(v3Token, 'v3')));
	}

	// Create a reCAPTCHA field for the form can be overridden with custom props
	createRecaptchaField(
		shouldUseLabel: boolean = true,
		props?: Partial<FormlyFieldConfig['props']>,
	): FormlyFieldConfig {
		const formlyField: FormlyFieldConfig = {
			id: 'token',
			key: 'token',
			props: {
				labelExtras: {
					alignment: 'left-aligned',
					width: 'sm:!w-1/4',
					contentWidth: 'sm:!w-3/4',
					noPadding: true,
				},
				noMargin: true,
				errorMode: 'handled',
				required: true,
				siteKey: this.recaptchaV2Key,
				size: 'default',
				...props,
			},
			type: 'recaptchaV2',
		};

		if (shouldUseLabel) {
			formlyField.wrappers = ['label'];
		}

		return formlyField;
	}

	// Add a reCAPTCHA field to the form, if useRecaptchaV2 is true and the form does not have a recaptcha field, add it
	addRecaptchaToForm(
		formFields: FormlyFieldConfig[],
		shouldUseRecaptchaV2: boolean,
		shouldUseLabel: boolean = true,
		customProps?: Partial<FormlyFieldConfig['props']>,
	): FormlyFieldConfig[] {
		const hasRecaptcha = formFields.some((field) => field.id === 'token');

		if (shouldUseRecaptchaV2 && !hasRecaptcha) {
			const newFields = [
				...formFields,
				this.createRecaptchaField(shouldUseLabel, customProps),
			];
			return newFields;
		}

		return formFields;
	}
}
